<template>
<div id="app" :class="appClass">
    <router-view v-if="!isPlannedDowntime"/>
    <PlannedDowntime v-else/>
    <GlobalSVG/>
</div>
</template>

<script>
import tippy, { updateAll } from '@/tippy'
import { mapState, mapGetters } from 'vuex'

import * as M from '@/store/mutations'
import GlobalSVG from '@/components/GlobalSVG'
import PlannedDowntime from '@/views/PlannedDowntime'
import { sleep } from '@shared/utils'

export default {
    name: 'App',
    computed: {
        ...mapState([
            'channel',
            'twitch',
            'isPlannedDowntime'
        ]),
        ...mapGetters([
            'channelId',
            'userId',
            'isBroadcaster',
            'isPopoutWindow',
            'isVideoComponentWindow',
            'isMobile',
        ]),
        appClass: vm => ({
            'dark-theme': vm.isDarkTheme,
            'is-popout-window': vm.isPopoutWindow,
            'is-video-component-window': vm.isVideoComponentWindow,
            'is-mobile': vm.isMobile,
        }),
        isDarkTheme () {
            const { mode } = this.channel.theme
            const { theme } = this.twitch.context

            return (mode === 'user' && theme === 'dark') || mode === 'dark'
        },
        // NOTE: I tried returning an object like { channelId, userId },
        // but the watcher was firing even though value equality didn't change.
        // Maybe reference equality changed?
        channelAndUserId: vm => `${vm.channelId}_${vm.userId}`,
    },
    watch: {
        // Wait for the twitch helper js to provide a channelId. This kicks
        // off the entire app.
        channelId: {
            async handler (newId, oldId) {
                if (newId && newId !== oldId) {
                    await this.$store.dispatch('fetchChannel')

                    await sleep(3000) // hack way to wait for all api calls to finish.
                    const { state, getters } = this.$store
                    debug('All data fetched', jsonCopy({ state, getters }))
                }
            },
            immediate: true
        },
        channelAndUserId () {
            // Need user's last post to check for isAllowedToSuggest
            if (this.channelId && this.userId) {
                this.$store.dispatch('fetchUser')
                this.$store.dispatch('fetchNotifications')
            }
        },
        'channel.theme' () {
            this.applyTheme(this.channel.theme)

            // Opposite theme for tooltips look better,
            const props = {
                theme: this.isDarkTheme ? 'light-border' : 'dark',
            }

            tippy.setDefaultProps(props)
            updateAll(props)
        },
    },
    created () {
        // api calls only occur once the twitch helper provides the jwt,
        // which may take a second. Just start the loading icon ASAP.
        // NOTE: I tried defaulting the loading properties to "true",
        // but it felt wrong.
        const payload = { isLoading: true }
        this.$store.commit(M.TOGGLE_LOADING_SUGGESTIONS, payload)
        this.$store.commit(M.TOGGLE_LOADING_CHANNEL, payload)
    },
    methods: {
        applyTheme (theme) {
            const { color, colorSecondary, colorContrast } = theme
            const $root = document.querySelector(':root')
            $root.style.setProperty('--theme-color', color)
            $root.style.setProperty('--theme-color-secondary', colorSecondary)
            $root.style.setProperty('--theme-color-contrast', colorContrast)
        }
    },
    components: {
        PlannedDowntime,
        GlobalSVG,
    }
}
</script>

<style lang="scss" scoped>
</style>
